/* ================================================================
   COREVIAN UNIVERSITY — WORKFORCE ATTENTION ENGINE
   ================================================================

   PURPOSE
   Convert already-loaded authoritative workforce-readiness evidence
   into consistent, read-only attention indicators.

   THIS ENGINE DOES NOT:
   - query Supabase
   - write to Supabase
   - send email
   - change qualification
   - change Operational Authorization
   - change endorsements
   - change controlled-document assignments
   - change recertification records

   Authoritative records remain authoritative.

   DATE-BASED ATTENTION TIERS
   --------------------------
   30–15 days : UPCOMING
   14–8 days  : WARNING
   7–1 days   : URGENT
   0 days     : DUE TODAY
   < 0 days   : PAST DUE

   Calendar-day comparison is used so a requirement due today does
   not become past due merely because its timestamp has passed.

   CONTROLLED-DOCUMENT ASSIGNMENT ELIGIBILITY (confirmed against
   the live corevian-university-qualification-logic.js Rules Engine):
   an outstanding or overdue controlled-document assignment blocks
   Assignment Eligibility. This is an aggregate, per-employee gate
   (employee.document_counts.overdue / .outstanding), not a property
   of any single document. No per-document "affects_authorization"
   flag exists or should be inferred from acknowledgment_required /
   training_required / competency_required — those describe what a
   document version requires, not whether it gates authorization.
   Assignment Eligibility and Operational Authorization remain
   separate decisions; this engine states the former, never implies
   the latter.
   ================================================================ */

(function () {
  'use strict';

  const DAY_MS = 24 * 60 * 60 * 1000;

  const ATTENTION_LEVELS = Object.freeze({
    PAST_DUE: 'past_due',
    DUE_TODAY: 'due_today',
    URGENT: 'urgent',
    WARNING: 'warning',
    UPCOMING: 'upcoming'
  });

  const ATTENTION_PRIORITY = Object.freeze({
    past_due: 1,
    due_today: 2,
    urgent: 3,
    warning: 4,
    upcoming: 5
  });

  function normalizeCalendarDate(value) {
    if (!value) return null;

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return null;
    }

    return new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    );
  }

  function getToday() {
    const now = new Date();

    return new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate()
    );
  }

  function daysUntilDate(value, todayOverride) {
    const dueDate = normalizeCalendarDate(value);

    if (!dueDate) return null;

    const today =
      normalizeCalendarDate(todayOverride) ||
      getToday();

    return Math.round(
      (dueDate.getTime() - today.getTime()) / DAY_MS
    );
  }

  function classifyDueDate(value, todayOverride) {
    const daysRemaining = daysUntilDate(value, todayOverride);

    if (daysRemaining === null) {
      return null;
    }

    if (daysRemaining < 0) {
      return {
        level: ATTENTION_LEVELS.PAST_DUE,
        label: 'Past Due',
        daysRemaining
      };
    }

    if (daysRemaining === 0) {
      return {
        level: ATTENTION_LEVELS.DUE_TODAY,
        label: 'Due Today',
        daysRemaining
      };
    }

    if (daysRemaining <= 7) {
      return {
        level: ATTENTION_LEVELS.URGENT,
        label: 'Urgent',
        daysRemaining
      };
    }

    if (daysRemaining <= 14) {
      return {
        level: ATTENTION_LEVELS.WARNING,
        label: 'Warning',
        daysRemaining
      };
    }

    if (daysRemaining <= 30) {
      return {
        level: ATTENTION_LEVELS.UPCOMING,
        label: 'Upcoming',
        daysRemaining
      };
    }

    return null;
  }

  function isControlledDocumentComplete(assignment) {
    if (!assignment || typeof assignment !== 'object') {
      return false;
    }

    return (
      assignment.status === 'completed' ||
      !!assignment.completed_at ||
      assignment.acknowledged === true
    );
  }

  function isControlledDocumentWaived(assignment) {
    return !!assignment && assignment.status === 'waived';
  }

  /* --------------------------------------------------------------
     CORRECTED per findings:
     - Does not assume every document requires a "required
       acknowledgment" — acknowledgment_required varies per version,
       and Mark as Read is deliberately never a substitute for it.
     - States the real, confirmed gate: any outstanding/overdue
       controlled-document assignment blocks Assignment Eligibility,
       as an aggregate condition — not something this specific
       document uniquely causes, and never framed as an Operational
       Authorization change.
     -------------------------------------------------------------- */
  const DOCUMENT_REQUIRED_ACTION =
    'Complete the assigned controlled document requirement. ' +
    'Outstanding or overdue controlled-document requirements ' +
    'prevent Assignment Eligibility until resolved.';

  function buildDocumentAttention(employee, todayOverride) {
    const assignments = Array.isArray(employee?.document_assignments)
      ? employee.document_assignments
      : [];

    const alerts = [];

    assignments.forEach(assignment => {
      if (!assignment) return;

      const completed = isControlledDocumentComplete(assignment);
      const waived = isControlledDocumentWaived(assignment);

      if (completed || waived) {
        return;
      }

      const tier = classifyDueDate(
        assignment.due_at,
        todayOverride
      );

      if (!tier) {
        return;
      }

      alerts.push({
        category: 'controlled_document',
        level: tier.level,
        label: tier.label,
        daysRemaining: tier.daysRemaining,

        employeeUserId: employee?.user_id || null,
        employeeName: employee?.full_name || 'Employee',

        recordId: assignment.assignment_id || null,
        requirementCode:
          assignment.document_code || 'Controlled Document',
        requirementName:
          assignment.title || 'Controlled Document',

        dueAt: assignment.due_at || null,

        requiredAction: DOCUMENT_REQUIRED_ACTION,

        source: 'document_assignments'
      });
    });

    return alerts;
  }

  function isRecertificationResolved(item) {
    if (!item || typeof item !== 'object') {
      return true;
    }

    return (
      item.status === 'not_required'
    );
  }

  function buildRecertificationAttention(employee, todayOverride) {
    const items = Array.isArray(employee?.recertification_items)
      ? employee.recertification_items
      : [];

    const alerts = [];

    items.forEach(item => {
      if (!item || isRecertificationResolved(item)) {
        return;
      }

      const tier = classifyDueDate(
        item.due_at,
        todayOverride
      );

      if (!tier) {
        return;
      }

      alerts.push({
        category: 'recertification',
        level: tier.level,
        label: tier.label,
        daysRemaining: tier.daysRemaining,

        employeeUserId: employee?.user_id || null,
        employeeName: employee?.full_name || 'Employee',

        recordId: item.id || null,
        requirementCode:
          item.requirement_code || 'Renewal Requirement',
        requirementName:
          item.requirement_name || 'Renewal Requirement',

        dueAt: item.due_at || null,

        requiredAction:
          'Complete and document the applicable recertification or renewal requirement.',

        source: 'recertification_items'
      });
    });

    return alerts;
  }

  function buildQualificationAttention(employee) {
    if (
      !employee ||
      typeof window.explainQualificationStatus !== 'function'
    ) {
      return [];
    }

    let explanation;

    try {
      explanation = window.explainQualificationStatus(employee);
    } catch (error) {
      return [];
    }

    if (!explanation) {
      return [];
    }

    const alerts = [];

    const blockers = Array.isArray(explanation.blockers)
      ? explanation.blockers
      : [];

    const warnings = Array.isArray(explanation.warnings)
      ? explanation.warnings
      : [];

    blockers.forEach(blocker => {
      alerts.push({
        category: 'qualification_blocker',
        level: 'action_required',
        label: 'Action Required',

        employeeUserId: employee?.user_id || null,
        employeeName: employee?.full_name || 'Employee',

        control: blocker?.control || 'Qualification',
        status: blocker?.status || null,
        explanation: blocker?.explanation || '',
        requiredAction:
          blocker?.nextAction ||
          'Review the qualification requirement.',

        dueAt: null,
        daysRemaining: null,

        source: 'authorization_explanation'
      });
    });

    warnings.forEach(warning => {
      alerts.push({
        category: 'qualification_warning',
        level: 'attention',
        label: 'Attention',

        employeeUserId: employee?.user_id || null,
        employeeName: employee?.full_name || 'Employee',

        control: warning?.control || 'Qualification',
        status: warning?.status || null,
        explanation: warning?.explanation || '',
        requiredAction:
          warning?.nextAction ||
          'Review the qualification warning.',

        dueAt: null,
        daysRemaining: null,

        source: 'authorization_explanation'
      });
    });

    return alerts;
  }

  function attentionSortValue(alert) {
    if (!alert) return 999;

    if (
      Object.prototype.hasOwnProperty.call(
        ATTENTION_PRIORITY,
        alert.level
      )
    ) {
      return ATTENTION_PRIORITY[alert.level];
    }

    if (alert.level === 'action_required') {
      return 2;
    }

    if (alert.level === 'attention') {
      return 4;
    }

    return 999;
  }

  function sortAttentionItems(items) {
    return [...items].sort((a, b) => {
      const priorityDifference =
        attentionSortValue(a) - attentionSortValue(b);

      if (priorityDifference !== 0) {
        return priorityDifference;
      }

      const aDays =
        typeof a.daysRemaining === 'number'
          ? a.daysRemaining
          : Number.MAX_SAFE_INTEGER;

      const bDays =
        typeof b.daysRemaining === 'number'
          ? b.daysRemaining
          : Number.MAX_SAFE_INTEGER;

      return aDays - bDays;
    });
  }

  function computeEmployeeAttention(employee, options) {
    const settings = options || {};
    const todayOverride = settings.today || null;

    if (!employee || typeof employee !== 'object') {
      return {
        employeeUserId: null,
        employeeName: null,
        hasAttention: false,
        highestSeverity: null,
        items: [],
        counts: {
          past_due: 0,
          due_today: 0,
          urgent: 0,
          warning: 0,
          upcoming: 0,
          action_required: 0,
          attention: 0
        }
      };
    }

    const items = sortAttentionItems([
      ...buildDocumentAttention(employee, todayOverride),
      ...buildRecertificationAttention(employee, todayOverride),
      ...buildQualificationAttention(employee)
    ]);

    const counts = {
      past_due: 0,
      due_today: 0,
      urgent: 0,
      warning: 0,
      upcoming: 0,
      action_required: 0,
      attention: 0
    };

    items.forEach(item => {
      if (
        Object.prototype.hasOwnProperty.call(
          counts,
          item.level
        )
      ) {
        counts[item.level]++;
      }
    });

    return {
      employeeUserId: employee.user_id || null,
      employeeName: employee.full_name || 'Employee',
      hasAttention: items.length > 0,
      highestSeverity:
        items.length > 0 ? items[0].level : null,
      items,
      counts
    };
  }

  function computeWorkforceAttention(rows, options) {
    const employees = Array.isArray(rows) ? rows : [];

    const results = employees.map(employee =>
      computeEmployeeAttention(employee, options)
    );

    const items = sortAttentionItems(
      results.flatMap(result => result.items)
    );

    const counts = {
      employees: employees.length,
      employees_needing_attention:
        results.filter(result => result.hasAttention).length,
      total_alerts: items.length,
      past_due: 0,
      due_today: 0,
      urgent: 0,
      warning: 0,
      upcoming: 0,
      action_required: 0,
      attention: 0
    };

    items.forEach(item => {
      if (
        Object.prototype.hasOwnProperty.call(
          counts,
          item.level
        )
      ) {
        counts[item.level]++;
      }
    });

    return {
      generatedAt: new Date().toISOString(),
      counts,
      employees: results,
      items
    };
  }

  window.CorevianAttentionEngine = Object.freeze({
    classifyDueDate,
    daysUntilDate,
    computeEmployeeAttention,
    computeWorkforceAttention
  });

})();